/**
 * useFieryAgent — print-agent connectivity for the DTF film drop.
 *
 * Reuses useAgentPort (BLU-410 5-port discovery) to find the local agent, then
 * picks the route from the agent's /status:
 *   - Agents from BLU-1338 on carry a `filmDropFolder` key on /status (always
 *     present, null when no folder). drop() POSTs /film/drop. They have NO done
 *     events (no folder watcher), so drainDone() makes no request at all.
 *   - 2.0.14 agents (no `filmDropFolder` key) keep the old routes: drop() POSTs
 *     /fiery/drop and drainDone() GETs /fiery/done, whose events the
 *     (authenticated) station relays to filmPrint.markFilmPrinted.
 * Both drop routes take the same body and answer the same shape.
 *
 * The agent is a thin relay: these calls move files + read events. NO garment/
 * film status is mutated by the agent — the station owns the tRPC relay.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAgentPort } from '@/features/print-station/hooks/useAgentPort';

const PROBE_TIMEOUT_MS = 2000;
const STATUS_POLL_MS = 30_000;

export interface FieryDoneEvent {
  garmentUnitId: string | null;
  filePath: string;
  via: 'source_removed' | 'moved_to_processed';
  at: string;
}

interface DropArgs {
  filename: string;
  bytesBase64?: string;
  presignedUrl?: string;
}

interface DropResult {
  success: boolean;
  destPath?: string;
  error?: string;
}

export function useFieryAgent() {
  const { port, status: portStatus, retry: retryPortDiscovery } = useAgentPort();
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasDtf, setHasDtf] = useState(false);
  const [version, setVersion] = useState<string | null>(null);
  const [hotFolder, setHotFolder] = useState<string | null>(null);
  /** true when /status carries a `filmDropFolder` key: this agent serves /film/drop. */
  const [hasFilmRoute, setHasFilmRoute] = useState(false);

  const baseUrl = port !== null ? `http://127.0.0.1:${port}` : null;

  // Probe /status for connectivity + the 'dtf' capability. The agent lazily
  // re-validates FIERY_HOT_FOLDER on each /status, so a folder created after
  // the agent booted flips hasDtf (and surfaces its path) on the next poll.
  useEffect(() => {
    let cancelled = false;
    if (portStatus === 'discovering') {
      setIsLoading(true);
      return () => { cancelled = true; };
    }
    if (portStatus === 'not_found' || baseUrl === null) {
      setIsConnected(false);
      setHasDtf(false);
      setVersion(null);
      setHotFolder(null);
      setHasFilmRoute(false);
      setIsLoading(false);
      return () => { cancelled = true; };
    }

    const probe = async () => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
        const res = await fetch(`${baseUrl}/status`, { signal: controller.signal });
        clearTimeout(timeout);
        if (cancelled) return;
        if (res.ok) {
          const data = await res.json() as {
            ok?: boolean;
            version?: string;
            capabilities?: string[];
            filmDropFolder?: string | null;
            fieryHotFolder?: string;
          };
          setIsConnected(!!data.ok);
          setHasDtf((data.capabilities ?? []).includes('dtf'));
          setVersion(data.version ?? null);
          setHasFilmRoute(
            typeof data === 'object' && data !== null && Object.prototype.hasOwnProperty.call(data, 'filmDropFolder'),
          );
          setHotFolder(data.filmDropFolder ?? data.fieryHotFolder ?? null);
        } else {
          setIsConnected(false);
          setHasDtf(false);
          setHotFolder(null);
          setHasFilmRoute(false);
        }
      } catch {
        if (!cancelled) { setIsConnected(false); setHasDtf(false); setHotFolder(null); setHasFilmRoute(false); }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void probe();
    const interval = setInterval(() => void probe(), STATUS_POLL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, [baseUrl, portStatus]);

  const drop = useCallback(async (args: DropArgs): Promise<DropResult> => {
    if (!baseUrl) return { success: false, error: 'agent_unreachable' };
    try {
      const res = await fetch(`${baseUrl}${hasFilmRoute ? '/film/drop' : '/fiery/drop'}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(args),
        signal: AbortSignal.timeout(90_000),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({})) as { error?: string };
        return { success: false, error: body.error ?? `HTTP ${res.status}` };
      }
      const body = await res.json() as { destPath?: string };
      return { success: true, destPath: body.destPath };
    } catch (err) {
      return { success: false, error: err instanceof Error ? err.message : String(err) };
    }
  }, [baseUrl, hasFilmRoute]);

  const drainDone = useCallback(async (): Promise<FieryDoneEvent[]> => {
    if (!baseUrl) return [];
    // New agents have no done events: zero local requests on every poll.
    if (hasFilmRoute) return [];
    try {
      const res = await fetch(`${baseUrl}/fiery/done`, { signal: AbortSignal.timeout(PROBE_TIMEOUT_MS) });
      if (!res.ok) return [];
      const body = await res.json() as { events?: FieryDoneEvent[] };
      return body.events ?? [];
    } catch {
      return [];
    }
  }, [baseUrl, hasFilmRoute]);

  // Memoize the returned object so its identity is stable across renders. The
  // FilmPrintPage done-poll useEffect lists `agent` in its deps; without this,
  // a fresh object literal every render would tear down + recreate the 4s
  // setInterval on every render, silently breaking auto-done detection (Fix 1 —
  // same class as the documented useScanFeedback unstable-object gotcha). drop /
  // drainDone are useCallbacks and retryPortDiscovery comes from useAgentPort,
  // so the memo only changes when an actual value changes.
  return useMemo(
    () => ({
      isConnected,
      isLoading,
      /** true only when the agent validated its film drop folder (dtf capability). */
      hasDtf,
      version,
      /** The validated drop destination reported by the agent (null when dtf is off). */
      hotFolder,
      /** true when this agent serves /film/drop (and has no done events). */
      hasFilmRoute,
      drop,
      drainDone,
      retryPortDiscovery,
    }),
    [isConnected, isLoading, hasDtf, version, hotFolder, hasFilmRoute, drop, drainDone, retryPortDiscovery],
  );
}
